import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useCurrentUser } from "../../../hooks/useCurrentUser"
import { useRunWhenReady } from "../../../hooks/useRunWhenReady"
import { detectPlatform, getCsrfToken } from "../utils"
import { SignInModal } from "./SignInModal"
import { WalletGenerationModal } from "./WalletGenerationModal"
import type { WalletClaimUrls, LoyaltyProgram } from "../types"

interface WalletButtonProps {
  loyaltyPrograms: LoyaltyProgram[]
  loyaltyCardGenerateUrl: string
  walletClaimUrls: WalletClaimUrls
  coffeeShopSlug: string
  appleAuthUrl: string
  googleAuthUrl: string
  googleWalletEnabled: boolean
}

export function WalletButton({
  loyaltyPrograms,
  loyaltyCardGenerateUrl,
  walletClaimUrls,
  coffeeShopSlug,
  appleAuthUrl,
  googleAuthUrl,
  googleWalletEnabled,
}: WalletButtonProps) {
  const { data: meData, isLoading: meLoading } = useCurrentUser()
  const current_user = meData?.current_user ?? null

  const [platform, setPlatform] = useState<"apple" | "google" | null>(null)
  const [selectedProgramUuid, setSelectedProgramUuid] = useState(
    loyaltyPrograms[0]?.uuid ?? ""
  )
  const [showSignIn, setShowSignIn] = useState(false)
  const [showDesktopInfo, setShowDesktopInfo] = useState(false)
  const [showProgramPicker, setShowProgramPicker] = useState(false)
  const [showGenerationModal, setShowGenerationModal] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [modalStatus, setModalStatus] = useState<
    "generating" | "completed" | "failed"
  >("generating")
  const [modalError, setModalError] = useState("")
  // Apple-specific polling data
  const [applePollingData, setApplePollingData] = useState<{
    loyaltyCardId: number
    generationStatusUrl: string
  } | null>(null)

  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  const selectedProgram = loyaltyPrograms.find(
    (p) => p.uuid === selectedProgramUuid
  )

  function handleAddToWallet() {
    if (!selectedProgram) return

    setShowProgramPicker(false)

    if (!current_user) {
      setShowSignIn(true)
      return
    }

    if (!platform) {
      setShowDesktopInfo(true)
      return
    }

    enrollInProgram(selectedProgram)
  }

  const {
    run: addToWallet,
    pending: addingToWallet,
    cancel: cancelAddToWallet,
  } = useRunWhenReady(!meLoading, handleAddToWallet)

  // Don't show wallet button on Android if Google Wallet is not enabled
  if (platform === "google" && !googleWalletEnabled) return null

  function handleClick() {
    setShowProgramPicker(true)
  }

  async function enrollInProgram(program: LoyaltyProgram) {
    // Open modal immediately for both platforms
    setModalStatus("generating")
    setModalError("")
    setRedirectUrl(null)
    setApplePollingData(null)
    setShowGenerationModal(true)

    try {
      const res = await fetch(loyaltyCardGenerateUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ platform, program_uuid: program.uuid }),
      })

      if (!res.ok) {
        const data = await res.json()
        setModalError(data.error || "Failed to generate loyalty card")
        setModalStatus("failed")
        return
      }

      const data = await res.json()

      if (data.platform === "apple" && data.download_url) {
        // Set polling data — modal switches to polling mode
        setApplePollingData({
          loyaltyCardId: data.loyalty_card_id,
          generationStatusUrl: data.generation_status_url,
        })
        setRedirectUrl(data.download_url)
      } else if (data.platform === "google" && data.wallet_url) {
        setRedirectUrl(data.wallet_url)
        setModalStatus("completed")
      }
    } catch (err) {
      setModalError(
        err instanceof Error
          ? err.message
          : "Unable to create loyalty card. Please try again."
      )
      setModalStatus("failed")
    }
  }

  const baseClaimUrl =
    platform === "google" ? walletClaimUrls.google : walletClaimUrls.apple
  const claimUrl = selectedProgramUuid
    ? `${baseClaimUrl}${baseClaimUrl.includes("?") ? "&" : "?"}program_uuid=${selectedProgramUuid}`
    : baseClaimUrl

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={showGenerationModal}
        className="flex items-center justify-center space-x-2 w-full rounded-lg bg-black px-3.5 py-4 text-md font-semibold text-white shadow-sm hover:bg-foreground-emphasis text-center disabled:opacity-50 transition-all duration-150 hover:shadow-md active:scale-[0.98]"
      >
        <WalletIcon platform={platform} />
        <span>Get Loyalty Card</span>
      </button>

      <Dialog
        open={showProgramPicker}
        onOpenChange={(open) => {
          if (!open) cancelAddToWallet()
          setShowProgramPicker(open)
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Choose a Loyalty Program</DialogTitle>
            <DialogDescription>
              Select a loyalty program and add it to your wallet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {loyaltyPrograms.map((program) => (
              <label
                key={program.uuid}
                className={`flex items-center rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                  selectedProgramUuid === program.uuid
                    ? "border-foreground-strong bg-surface-subtle"
                    : "border-border-default hover:bg-surface-subtle"
                }`}
              >
                <input
                  type="radio"
                  name="loyalty_program"
                  value={program.uuid}
                  checked={selectedProgramUuid === program.uuid}
                  onChange={() => setSelectedProgramUuid(program.uuid)}
                  className="h-4 w-4 text-foreground-strong border-border-strong focus:ring-foreground-strong"
                />
                <div className="ml-3">
                  <p className="text-sm font-medium text-foreground-primary">
                    {program.name}
                  </p>
                  <p className="text-xs text-foreground-muted">
                    {program.stamps_required} stamps
                  </p>
                </div>
              </label>
            ))}
          </div>
          <Button
            onClick={addToWallet}
            disabled={!selectedProgramUuid || addingToWallet}
            aria-busy={addingToWallet}
            className="w-full mt-2"
          >
            {addingToWallet ? "Adding…" : "Add to Wallet"}
          </Button>
        </DialogContent>
      </Dialog>

      <SignInModal
        open={showSignIn}
        onOpenChange={setShowSignIn}
        title="Sign In Required"
        description="Please sign in to get your loyalty card. This helps us keep your card secure and linked to your account."
        appleAuthUrl={appleAuthUrl}
        googleAuthUrl={googleAuthUrl}
        origin={claimUrl}
      />

      <Dialog open={showDesktopInfo} onOpenChange={setShowDesktopInfo}>
        <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-sm">
          <DialogHeader className="text-center">
            <DialogTitle>Add to Wallet</DialogTitle>
            <DialogDescription>
              To add this loyalty card to your wallet, please open this page on
              your mobile device.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium text-center text-brand-strong break-all">
            petakopi.my/{coffeeShopSlug}
          </p>
          <Button onClick={() => setShowDesktopInfo(false)}>Got it</Button>
        </DialogContent>
      </Dialog>

      <WalletGenerationModal
        open={showGenerationModal}
        onClose={() => {
          setShowGenerationModal(false)
          setApplePollingData(null)
          setRedirectUrl(null)
        }}
        onRetry={() => {
          if (selectedProgram) enrollInProgram(selectedProgram)
        }}
        redirectUrl={redirectUrl}
        loyaltyCardId={applePollingData?.loyaltyCardId}
        generationStatusUrl={applePollingData?.generationStatusUrl}
        status={modalStatus}
        errorMessage={modalError}
      />
    </>
  )
}

export function WalletIcon({
  platform,
}: {
  platform: "apple" | "google" | null
}) {
  if (platform === "google") {
    // Official Google Wallet icon
    return (
      <svg
        className="h-6 w-6"
        viewBox="0 0 30 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30 10H0V5C0 2.28571 2.20183 0 4.81651 0H25.1835C27.7982 0 30 2.28571 30 5V10Z"
          fill="#34A853"
        />
        <path
          d="M30 14.0537H0V9.05371C0 6.33942 2.20183 4.05371 4.81651 4.05371H25.1835C27.7982 4.05371 30 6.33942 30 9.05371V14.0537Z"
          fill="#FBBC04"
        />
        <path
          d="M30 18.2468H0V13.2468C0 10.5325 2.20183 8.24683 4.81651 8.24683H25.1835C27.7982 8.24683 30 10.5325 30 13.2468V18.2468Z"
          fill="#EA4335"
        />
        <path
          d="M0 11.2752L19.0411 15.6789C21.2329 16.2294 23.6986 15.6789 25.4795 14.3028L30 11V21.3211C30 23.9358 27.8082 26 25.2055 26H4.79449C2.19183 26 0 23.9358 0 21.3211V11.2752Z"
          fill="url(#gw_grad)"
        />
        <defs>
          <linearGradient
            id="gw_grad"
            x1="13.5703"
            y1="18.7864"
            x2="-2.26914"
            y2="36.9133"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4285F4" />
            <stop offset="1" stopColor="#1B74E8" />
          </linearGradient>
        </defs>
      </svg>
    )
  }

  // Apple Wallet icon (stacked cards) — fallback for desktop too
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 120 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="5" y="5" width="110" height="90" rx="12" fill="#D4D0C8" />
      <rect x="15" y="12" width="90" height="20" rx="6" fill="#4A9DE8" />
      <rect x="15" y="26" width="90" height="20" rx="6" fill="#F5A623" />
      <rect x="15" y="40" width="90" height="20" rx="6" fill="#5CB85C" />
      <path
        d="M15 54C15 50.6863 17.6863 48 21 48H99C102.314 48 105 50.6863 105 54V60H15V54Z"
        fill="#E8636A"
      />
      <path
        d="M5 58V83C5 89.6274 10.3726 95 17 95H103C109.627 95 115 89.6274 115 83V58H65C65 65.1797 59.1797 71 52 71H68C60.8203 71 55 65.1797 55 58H5Z"
        fill="#D4D0C8"
      />
    </svg>
  )
}
